import type { StrategyPlanView } from '@/spaces/campaigns/useStrategyPlanView';

// Type-only imports, deliberately. A value import here would pull a component module in, and
// those call the Spectrum `style` macro at module scope, which vitest cannot transform. The
// suite for this file would then throw at import time instead of failing on a wrong step list.

export type StrategyStep =
	| { kind: 'idea' }
	| { kind: 'success' }
	| { kind: 'audience' }
	| { kind: 'campaign'; campaignId: string; ordinal: number }
	| { kind: 'recap' };

/** Stable identity for one step, so a cursor can survive the plan changing under it. */
export const strategyStepKey = (step: StrategyStep): string =>
	step.kind === 'campaign' ? `campaign:${step.campaignId}` : step.kind;

/** The short word the step spine shows.
 *
 *  The width budget is arithmetic, not taste: every cell is `min-w-0 flex-1`, so a cell is
 *  `(panel width - 96px) / N`. A plan can reach 10 steps (MAX_CAMPAIGNS is 6, plus idea, success,
 *  the optional audience and the recap), which at the default split is about 49px — roughly 7
 *  characters at the 12px label size. A campaign TITLE is 20 to 25 characters in real plans and
 *  fits at no step count, which is why it is not used here.
 *
 *  "Move 1" is 6 characters and clears that budget. The word is the surface's own: the step body
 *  calls a campaign "one of the moves that gets you there". A bare ordinal cleared the budget too,
 *  and an owner rejected it — a number alone says nothing about what the step holds. */
export const strategyStepLabel = (step: StrategyStep): string => {
	switch (step.kind) {
		case 'idea':
			return 'Idea';
		case 'success':
			return 'Success';
		case 'audience':
			return 'Audience';
		case 'campaign':
			return `Move ${step.ordinal}`;
		case 'recap':
			return 'Start';
	}
};

export const strategyStepAdvanceLabel = (step: StrategyStep): string => {
	switch (step.kind) {
		case 'idea':
			return 'This is the right plan';
		case 'success':
			return 'This is the right goal';
		case 'audience':
			return "That's who we want";
		case 'campaign':
			return 'Got it';
		case 'recap':
			return '';
	}
};

/** What the back control announces. Never "Back to …": the panel header already owns that
 *  wording for the control that LEAVES the panel, so a shared prefix gives two controls in one
 *  panel near-identical accessible names. */
export const strategyStepBackLabel = (step: StrategyStep): string =>
	`Previous: ${strategyStepLabel(step)}`;

export const buildStrategySteps = (
	view: StrategyPlanView | null | undefined,
): StrategyStep[] => {
	// Success is its OWN part. It shared the idea part until an owner pointed out that a plan and
	// the number it is judged on are two separate decisions, and pairing them made both harder to
	// read. It sits second because the number is what the assistant opens the conversation on.
	const steps: StrategyStep[] = [{ kind: 'idea' }, { kind: 'success' }];

	// The stored run keeps `direction` as an untyped dict, so pydantic never validates the
	// audience on the read path. The shipped AudienceSection guards the same field.
	if (view?.strategy?.audience?.who?.trim()) steps.push({ kind: 'audience' });

	(view?.campaigns ?? []).forEach((campaign, index) => {
		if (!campaign?.id) return;
		steps.push({
			kind: 'campaign',
			campaignId: campaign.id,
			ordinal: index + 1,
		});
	});

	steps.push({ kind: 'recap' });
	return steps;
};

/** Where the owner should land after the plan changed under them. Anchors on the step KEY, so a
 *  dropped campaign does not silently re-point an in-range cursor at different content. Falls
 *  back to a clamp only when the step the owner was reading is gone. */
export const resolveStrategyStepIndex = (
	steps: StrategyStep[],
	previousKey: string | null,
	currentIndex: number,
): number => {
	if (steps.length === 0) return 0;
	if (previousKey) {
		const found = steps.findIndex((s) => strategyStepKey(s) === previousKey);
		if (found >= 0) return found;
	}
	return Math.min(Math.max(currentIndex, 0), steps.length - 1);
};

/** The two halves of a plan statement, and whether the split actually worked. */
export type StatementHalves = {
	plan: string;
	notThis: string | null;
};

// Validator rule R23 forces `direction.statement` into a fixed shape: a "we will …" clause naming
// the target, the audience and the offer, then a contrast pivot. So the string is 400 to 500
// characters of two welded ideas, and rendering it whole is the wall this walk exists to remove.
//
// The pivot is guaranteed prose, not a delimiter, so the split is best-effort by design. When it
// fails the caller shows ONE clamped block. The clamp is the contract; the split only improves it.
const STATEMENT_PIVOT = /\bwe will win because\b/i;

export const splitStatement = (
	statement: string | null | undefined,
): StatementHalves | null => {
	const text = statement?.trim();
	if (!text) return null;
	const m = STATEMENT_PIVOT.exec(text);
	// Refuse a pivot that lands in the first fifth of the string. A "plan" half of a dozen
	// characters is not a half, and splitting there reads as a formatting fault.
	if (!m || m.index < text.length * 0.2) return { plan: text, notThis: null };
	const plan = text.slice(0, m.index).trim();
	const notThis = text.slice(m.index).trim();
	if (!plan || !notThis) return { plan: text, notThis: null };
	return { plan, notThis };
};
